import { useEffect, useState } from "react";
import TopNav from "../components/TopNav.jsx";
import ProductMark from "../components/ProductMark.jsx";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

/**
 * AdSmith — the honesty-stacked campaign wizard.
 * PUBLIC draft rail: visitor drafts a campaign brief. AdSmith grades against
 * forbidden targeting classes (Codex-defined) and dark-pattern ad copy.
 */
export default function AdSmithPage() {
  const [meter, setMeter] = useState(null);
  const [card, setCard] = useState(null);
  const [goal, setGoal] = useState("");
  const [network, setNetwork] = useState("generic");
  const [budgetCap, setBudgetCap] = useState(100);
  const [audienceNote, setAudienceNote] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    fetch(`${BACKEND}/api/sage/torch/meter`).then((r) => r.json()).then(setMeter).catch(() => {});
    fetch(`${BACKEND}/api/sage/torch/handshake`).then((r) => r.json()).then(setCard).catch(() => {});
  }, []);

  const refresh = () => fetch(`${BACKEND}/api/sage/torch/meter`).then((r) => r.json()).then(setMeter).catch(() => {});

  const submit = async (e) => {
    e.preventDefault();
    if (goal.trim().length < 3) { setErr("Goal too short. At least 3 characters."); return; }
    if (budgetCap <= 0) { setErr("Budget must be greater than 0."); return; }
    setBusy(true);
    setErr("");
    setResult(null);
    try {
      const r = await fetch(`${BACKEND}/api/sage/torch/wizard`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          goal: goal.trim(),
          network,
          budget_cap_usd: Number(budgetCap),
          audience_note: audienceNote.trim(),
        }),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setResult(d);
      refresh();
    } catch (e2) {
      setErr(String(e2.message || e2));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-black" data-testid="adsmith-page">
      <TopNav />
      <main className="max-w-3xl mx-auto px-6 py-16 space-y-10">
        <header className="space-y-3">
          <div className="text-xs uppercase tracking-wider text-neutral-500">Live · public draft rail</div>
          <div className="flex items-start gap-4">
            <div className="text-marquee-accent shrink-0"><ProductMark slug="marquee" size={48} /></div>
            <h1 className="text-4xl sm:text-5xl font-normal tracking-tight" data-testid="adsmith-title">AdSmith</h1>
          </div>
          <p className="text-lg text-neutral-700 leading-relaxed">
            Draft a campaign brief. AdSmith reads it for dark-pattern copy and
            forbidden targeting classes. Clean drafts get a green mark. Refused
            ones get plain-English reasons why. Every brief files a receipt on
            the public counter below.
          </p>
        </header>

        {/* Campaign form */}
        <section className="border border-neutral-300 p-5" data-testid="adsmith-draft-form">
          <h2 className="text-lg font-medium mb-3">Draft a campaign</h2>
          <form onSubmit={submit} className="space-y-3">
            <label className="block text-xs uppercase tracking-wider text-neutral-600">
              Goal (what should this campaign accomplish?)
              <textarea
                data-testid="adsmith-goal-input"
                value={goal}
                onChange={(e) => setGoal(e.target.value)}
                placeholder="Example: Announce our new $10/mo tier to prior visitors who never signed up."
                maxLength={400}
                rows={3}
                className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-black"
              />
            </label>
            <div className="flex gap-3 flex-wrap">
              <label className="text-xs text-neutral-600 uppercase tracking-wide">
                Network
                <select
                  data-testid="adsmith-network-select"
                  value={network}
                  onChange={(e) => setNetwork(e.target.value)}
                  className="ml-2 border border-neutral-300 px-2 py-1 text-sm"
                >
                  <option value="generic">generic</option>
                  <option value="meta">meta</option>
                  <option value="google">google</option>
                  <option value="reddit">reddit</option>
                  <option value="x">x</option>
                  <option value="mailer">mailer</option>
                </select>
              </label>
              <label className="text-xs text-neutral-600 uppercase tracking-wide">
                Budget cap (USD)
                <input
                  type="number"
                  min={1}
                  max={100000}
                  data-testid="adsmith-budget-input"
                  value={budgetCap}
                  onChange={(e) => setBudgetCap(parseFloat(e.target.value) || 0)}
                  className="ml-2 border border-neutral-300 px-2 py-1 text-sm w-24"
                />
              </label>
            </div>
            <label className="block text-xs uppercase tracking-wider text-neutral-600">
              Audience note (optional · describe who you want to reach)
              <input
                type="text"
                data-testid="adsmith-audience-input"
                value={audienceNote}
                onChange={(e) => setAudienceNote(e.target.value)}
                maxLength={400}
                className="mt-1 w-full border border-neutral-300 px-3 py-2 text-sm outline-none focus:border-black"
              />
            </label>
            <button
              type="submit"
              disabled={busy || goal.trim().length < 3 || budgetCap <= 0}
              data-testid="adsmith-draft-submit"
              className="border-2 border-black bg-black text-white px-5 py-2 text-xs uppercase tracking-wider font-bold hover:bg-white hover:text-black disabled:opacity-40 transition-colors"
            >
              {busy ? "Grading…" : "Draft and grade"}
            </button>
            {err && <div className="text-sm text-red-700" data-testid="adsmith-error">{err}</div>}
          </form>

          {result && (
            <div className="mt-5 border-t border-neutral-200 pt-4" data-testid="adsmith-result">
              <div className="flex justify-between items-center mb-2">
                <div className="text-xs uppercase tracking-wider text-neutral-500">AdSmith says</div>
                <div
                  data-testid="adsmith-grade-badge"
                  className={`text-[10px] uppercase tracking-wider px-2 py-0.5 border ${result.grade === "clean" ? "border-green-700 text-green-800" : "border-red-700 text-red-800"}`}
                >
                  {result.grade}
                </div>
              </div>
              <div className="text-sm text-neutral-800" data-testid="adsmith-verdict">{result.verdict}</div>
              {result.issues && result.issues.length > 0 && (
                <ul className="mt-2 text-xs text-neutral-700 list-disc pl-5">
                  {result.issues.map((i, idx) => <li key={idx}>{i}</li>)}
                </ul>
              )}
            </div>
          )}
        </section>

        {/* Live counters */}
        <section className="space-y-2" data-testid="adsmith-counters">
          <h2 className="text-lg font-medium">Live counters</h2>
          <div className="grid grid-cols-3 gap-4 text-sm">
            <div className="border border-neutral-200 px-4 py-3">
              <div className="text-2xl font-normal" data-testid="adsmith-campaigns-filed">{meter?.campaigns_filed ?? "—"}</div>
              <div className="text-xs text-neutral-500 uppercase tracking-wider mt-1">campaigns filed</div>
            </div>
            <div className="border border-neutral-200 px-4 py-3">
              <div className="text-2xl font-normal">{meter?.buys_fired ?? "—"}</div>
              <div className="text-xs text-neutral-500 uppercase tracking-wider mt-1">buys fired</div>
            </div>
            <div className="border border-neutral-200 px-4 py-3">
              <div className="text-2xl font-normal">
                {meter?.total_spent_usd != null ? `$${meter.total_spent_usd.toFixed(2)}` : "—"}
              </div>
              <div className="text-xs text-neutral-500 uppercase tracking-wider mt-1">total spent</div>
            </div>
          </div>
          <div className="text-xs text-neutral-500 pt-1">
            Zeros are zeros. No mock spend. Network buy rail opens when the Founder seats real keys.
          </div>
        </section>

        {/* Forbidden classes list */}
        {card?.forbidden_targeting_classes && (
          <section className="space-y-2" data-testid="adsmith-forbidden">
            <h2 className="text-lg font-medium">Targeting classes AdSmith refuses</h2>
            <ul className="text-sm text-neutral-800 space-y-1">
              {card.forbidden_targeting_classes.map((f) => (
                <li key={f} className="border-l-2 border-red-400 pl-3">{f.replace(/_/g, " ")}</li>
              ))}
            </ul>
          </section>
        )}
      </main>
    </div>
  );
}
